/**
 * SEA — cartoon water: flat cel bands with wavy edges (pale surface, body, deep), an inked
 * horizon, rows of foam dashes riding the swell, and the sun/moon glare as a column of flat
 * stacked glints. After Ghost Circuit's puddles: flat tones with hard edges, a clean ink edge,
 * level streaks and crisp white glints, never a soft gradient.
 *
 * Layouts (band edges, foam rows, glare rows) are computed here once per frame and shared:
 * Canvas 2D draws them below, the GPU sea shader receives them as uniforms
 * (render/sky/shader-uniforms.js), so the two renderers match.
 *
 * Motion runs on the pause-aware wobble clock passed as `waveTime` (seconds), so the swell
 * laps at a steady pace whatever the in-game hour does, and stops when the game pauses.
 */

import {
  clamp01,
  lerpRgb,
  rgb,
  normalizeTimeOfDay,
  timeSec,
} from "./sky-utils.js";
import { INK, parseHex } from "../toon/palette.js";

// Sea palettes: surface (the pale near-horizon band), body, deep, foam dashes and the sheen
// streak just under the horizon.
var SEA_KEYFRAMES = [
  { t: 0, surface: [122, 198, 228], mid: [70, 156, 208], deep: [46, 108, 176], foam: [238, 250, 255], sheen: [196, 236, 250] },
  { t: 0.25, surface: [96, 212, 244], mid: [36, 166, 226], deep: [22, 116, 190], foam: [244, 253, 255], sheen: [196, 244, 255] },
  { t: 0.38, surface: [132, 206, 228], mid: [56, 152, 212], deep: [34, 104, 176], foam: [255, 248, 226], sheen: [255, 234, 190] },
  { t: 0.5, surface: [138, 134, 196], mid: [94, 88, 166], deep: [58, 52, 124], foam: [255, 216, 176], sheen: [255, 184, 150] },
  { t: 0.62, surface: [108, 92, 164], mid: [68, 58, 132], deep: [38, 34, 96], foam: [236, 186, 210], sheen: [212, 142, 172] },
  { t: 0.75, surface: [40, 74, 136], mid: [26, 50, 104], deep: [14, 30, 72], foam: [138, 170, 224], sheen: [74, 112, 178] },
  { t: 0.88, surface: [124, 110, 176], mid: [72, 68, 146], deep: [36, 40, 104], foam: [255, 214, 228], sheen: [226, 160, 192] },
  { t: 1, surface: [122, 198, 228], mid: [70, 156, 208], deep: [46, 108, 176], foam: [238, 250, 255], sheen: [196, 236, 250] },
];

/** Upper edge of the body and deep bands, as a fraction of the sea height. */
export var SEA_BAND_EDGES = [0.17, 0.46];
/** Most foam rows / glare rows either renderer draws (the shader loops to these). */
export var SEA_FOAM_MAX_ROWS = 12;
export var SEA_GLARE_MAX_ROWS = 16;

/** Inked horizon line width (px at ui-w=720). */
export var HORIZON_LINE_WIDTH_AT_720 = 2.6;
/** Pale sheen streak under the horizon ink (px at ui-w=720). */
export var HORIZON_SHEEN_WIDTH_AT_720 = 2;
/** How much of INK goes into the horizon line colour (the rest is the deep sea tone). */
export var HORIZON_INK_MIX = 0.55;
/** Kept for callers that size the old horizon bleed: the cartoon sea has no bleed. */
export var HORIZON_BLEED_WIDTH_AT_720 = 0;
/** Tiny AA on the sea edges (px at ui-w=720). */
export var HORIZON_SEAM_WIDTH_AT_720 = 1;

var INK_RGB = parseHex(INK);

export function horizonLineWidth(scaleUi) {
  var s = Math.max(0.05, Number(scaleUi) || 1);
  return Math.max(1.5, HORIZON_LINE_WIDTH_AT_720 * s);
}

export function horizonSeamHeight(width) {
  var w = Math.max(0, Number(width) || 0);
  return Math.max(0.5, (w / 720) * HORIZON_SEAM_WIDTH_AT_720);
}

/**
 * Sea colours for a colour phase.
 * @returns {{ surface: number[], mid: number[], deep: number[], foam: number[], sheen: number[], line: number[] }}
 */
export function seaColors(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  var out = null;
  for (var i = 0; i < SEA_KEYFRAMES.length - 1; i++) {
    var a = SEA_KEYFRAMES[i];
    var b = SEA_KEYFRAMES[i + 1];
    if (t >= a.t && t <= b.t) {
      var span = b.t - a.t;
      var u = span > 0 ? (t - a.t) / span : 0;
      out = {
        surface: lerpRgb(a.surface, b.surface, u),
        mid: lerpRgb(a.mid, b.mid, u),
        deep: lerpRgb(a.deep, b.deep, u),
        foam: lerpRgb(a.foam, b.foam, u),
        sheen: lerpRgb(a.sheen, b.sheen, u),
      };
      break;
    }
  }
  if (!out) {
    var last = SEA_KEYFRAMES[SEA_KEYFRAMES.length - 1];
    out = {
      surface: last.surface.slice(),
      mid: last.mid.slice(),
      deep: last.deep.slice(),
      foam: last.foam.slice(),
      sheen: last.sheen.slice(),
    };
  }
  out.line = lerpRgb(out.deep, INK_RGB, HORIZON_INK_MIX);
  return out;
}

function posMod(a, m) {
  return ((a % m) + m) % m;
}

/**
 * Wave on the upper edge of sea band `j` (0 = body band, 1 = deep band).
 * @returns {{ y: number, amp: number, len: number, phase: number }} edge base y, amplitude,
 *   wavelength (px) and phase (radians, already wrapped)
 */
export function seaBandEdge(j, seaY, seaH, scaleUi, waveTime) {
  var s = Math.max(0.05, Number(scaleUi) || 1);
  var t = Number(waveTime) || 0;
  var amp = Math.min((2.2 + 2.6 * j) * s, seaH * 0.05);
  var len = (170 + 90 * j) * s;
  var dir = j % 2 ? -1 : 1;
  return {
    y: seaY + seaH * SEA_BAND_EDGES[j],
    amp: amp,
    len: len,
    phase: posMod(j * 2.1 + dir * t * (0.45 + 0.2 * j), Math.PI * 2),
  };
}

/** y of a band edge at x. */
export function seaEdgeY(edge, x) {
  return edge.y + edge.amp * Math.sin((x / edge.len) * Math.PI * 2 + edge.phase);
}

/**
 * Foam rows for the swell: dashes riding a sine crest, longer, thicker and brighter toward
 * the viewer, each row drifting its own way.
 * @returns {{ y: number, amp: number, cycles: number, phase: number, period: number,
 *   thick: number, scroll: number, alpha: number, mix: number, index: number }[]}
 */
export function layoutSeaFoam(width, seaY, seaH, scaleUi, waveTime) {
  var w = Math.max(1, Number(width) || 1);
  var s = Math.max(0.05, Number(scaleUi) || 1);
  var t = Number(waveTime) || 0;
  if (!(seaH > 4)) return [];
  var n = Math.max(2, Math.min(SEA_FOAM_MAX_ROWS, Math.floor(seaH / (27 * s))));
  var rows = [];
  for (var wi = 0; wi < n; wi++) {
    var d = n > 1 ? wi / (n - 1) : 0.5;
    var period = (60 + 120 * d) * s;
    var dir = wi % 2 ? -1 : 1;
    var speed = (7 + 12 * d) * s;
    rows.push({
      index: wi,
      y: seaY + seaH * (0.075 + 0.86 * Math.pow(d, 1.45)),
      amp: Math.max(0.5, Math.min(seaH * 0.012, (1 + 2.2 * d) * s)),
      cycles: Math.max(2, w / ((150 + 130 * d) * s)),
      phase: posMod(wi * 1.7 + t * 1.1, Math.PI * 2),
      period: period,
      thick: Math.max(1, (1.3 + 2.7 * d) * s),
      // The dash pattern repeats every 60 periods (see foamDash), so the scroll wraps there.
      scroll: posMod(t * speed * dir, period * 60),
      alpha: 0.4 + 0.32 * d,
      mix: Math.min(1, d * 1.6),
    });
  }
  return rows;
}

/**
 * Dash `k` of foam row `row` (in the row's scrolled coordinate u = x + scroll), or null for a
 * gap. Integer arithmetic only, so the GPU shader (same formula) agrees exactly.
 * @returns {{ start: number, len: number }|null}
 */
export function foamDash(row, k) {
  var i = row.index;
  if (posMod(k + i, 5) === 3) return null;
  var len = row.period * (0.3 + 0.08 * posMod(k * 7 + i * 3, 4));
  var start = k * row.period + row.period * 0.12 * posMod(k * 3 + i, 3);
  return { start: start, len: len };
}

/** Crest y of a foam row at x. */
export function foamCrestY(row, x, width) {
  return row.y + Math.sin((x / width) * Math.PI * 2 * row.cycles + row.phase) * row.amp;
}

/**
 * Glare under the sun or moon: stacked flat glints, a long one per row with a short
 * satellite beside it, swaying and flickering, wider and dimmer toward the viewer.
 * @returns {{ y: number, x: number, half: number, thick: number, alpha: number, satX: number, satHalf: number }[]}
 */
export function layoutSeaGlare(opts) {
  var o = opts || {};
  var seaY = Number(o.seaLineY);
  var seaH = Number(o.seaH);
  var cx = Number(o.celestialX);
  if (!isFinite(seaY) || !isFinite(cx) || !(seaH > 4)) return [];
  var s = Math.max(0.05, Number(o.scaleUi) || 1);
  var strength = clamp01(o.strength != null ? o.strength : 1);
  if (strength <= 0.01) return [];
  var widthScale = clamp01(o.widthScale != null ? o.widthScale : 1);
  var t = Number(o.waveTime) || 0;
  var baseHalf = (o.isMoon ? 58 : 76) * s * widthScale;
  if (baseHalf <= 0.5) return [];
  var m = Math.max(4, Math.min(SEA_GLARE_MAX_ROWS, Math.round(seaH / (14 * s))));
  var rows = [];
  for (var j = 0; j < m; j++) {
    var d = (j + 0.5) / m;
    var flick = 0.74 + 0.26 * Math.sin(t * 2.6 + j * 2.3);
    var half = baseHalf * (0.2 + 0.9 * d) * flick;
    var thick = Math.max(1, (1.4 + 3.6 * d) * s);
    var sway = Math.sin(t * 0.85 + j * 0.55) * baseHalf * 0.09;
    var x = cx + sway;
    var side = j % 2 ? 1 : -1;
    var satHalf = half * (0.22 + 0.12 * (0.5 + 0.5 * Math.sin(t * 1.9 + j)));
    var gap = thick * 1.6 + 3 * s;
    rows.push({
      y: seaY + seaH * (0.03 + 0.94 * Math.pow(d, 1.35)),
      x: x,
      half: half,
      thick: thick,
      alpha: strength * (1 - 0.72 * d) * (0.8 + 0.2 * Math.sin(t * 3.3 + j * 1.3)),
      satX: x + side * (half + gap + satHalf),
      satHalf: satHalf,
    });
  }
  return rows;
}

/**
 * Glare colours: a tinted glint with a pale core. Warm gold by day, orange at sunset, cool
 * silver-blue under the moon.
 * @param {boolean} isMoon
 * @param {number} sunset 0..1
 */
export function glareColors(isMoon, sunset) {
  if (isMoon) return { tint: [170, 202, 255], core: [244, 250, 255] };
  var k = clamp01(sunset);
  return {
    tint: lerpRgb([255, 251, 224], [255, 190, 110], k),
    core: lerpRgb([255, 255, 255], [255, 240, 204], k),
  };
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} timeOfDay
 * @param {{ seaLineY?: number, waveTime?: number, scaleUi?: number }} [opts]
 */
export function drawSea(ctx, bounds, timeOfDay, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;

  var seaLineY = Number(opts.seaLineY);
  var seaY;
  var seaH;
  if (isFinite(seaLineY)) {
    seaY = seaLineY;
    seaH = Math.max(0, y + h - seaY);
  } else {
    seaH = h * 0.35;
    seaY = y + h - seaH;
  }
  if (seaH <= 0) return;
  var scaleUi = Number(opts.scaleUi);
  if (!isFinite(scaleUi) || scaleUi <= 0) scaleUi = Math.max(0.5, w / 720);
  var waveTime = Number(opts.waveTime);
  if (!isFinite(waveTime)) waveTime = normalizeTimeOfDay(timeOfDay) * 40;
  var colors = seaColors(timeOfDay);

  ctx.fillStyle = rgb(colors.surface);
  ctx.fillRect(x, seaY, w, seaH);
  var step = Math.max(4, 14 * scaleUi);
  var bandFill = [colors.mid, colors.deep];
  for (var j = 0; j < SEA_BAND_EDGES.length; j++) {
    var edge = seaBandEdge(j, seaY, seaH, scaleUi, waveTime);
    ctx.beginPath();
    ctx.moveTo(x, y + h);
    var px = 0;
    ctx.lineTo(x, seaEdgeY(edge, 0));
    while (px < w) {
      px = Math.min(w, px + step);
      ctx.lineTo(x + px, seaEdgeY(edge, px));
    }
    ctx.lineTo(x + w, y + h);
    ctx.closePath();
    ctx.fillStyle = rgb(bandFill[j]);
    ctx.fill();
  }

  drawSeaFoam(ctx, x, w, layoutSeaFoam(w, seaY, seaH, scaleUi, waveTime), colors);

  // The horizon: an ink line, with a pale sheen streak tucked under it.
  var lw = horizonLineWidth(scaleUi);
  var sheenW = Math.max(1, HORIZON_SHEEN_WIDTH_AT_720 * scaleUi);
  ctx.fillStyle = rgb(colors.sheen);
  ctx.fillRect(x, seaY + lw * 0.5, w, sheenW);
  ctx.fillStyle = rgb(colors.line);
  ctx.fillRect(x, seaY - lw * 0.5, w, lw);
}

function drawSeaFoam(ctx, x, w, rows, colors) {
  if (!rows.length || typeof ctx.stroke !== "function") return;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (var r = 0; r < rows.length; r++) {
    var row = rows[r];
    var col = lerpRgb(colors.sheen, colors.foam, row.mix);
    ctx.strokeStyle = rgb(col, row.alpha.toFixed(3));
    ctx.lineWidth = row.thick;
    ctx.beginPath();
    var k0 = Math.floor(row.scroll / row.period) - 1;
    var k1 = Math.floor((w + row.scroll) / row.period) + 1;
    for (var k = k0; k <= k1; k++) {
      var dash = foamDash(row, k);
      if (!dash) continue;
      var a = dash.start - row.scroll + row.thick * 0.5;
      var b = dash.start + dash.len - row.scroll - row.thick * 0.5;
      if (b < a) b = a;
      if (b < -row.thick || a > w + row.thick) continue;
      ctx.moveTo(x + a, foamCrestY(row, a, w));
      var steps = 3;
      for (var q = 1; q <= steps; q++) {
        var px = a + (b - a) * (q / steps);
        ctx.lineTo(x + px, foamCrestY(row, px, w));
      }
    }
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Sun/moon glare on the sea: stacked flat glints (see layoutSeaGlare).
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {{ celestialX: number, seaLineY: number, isMoon?: boolean, currentTime?: number,
 *   waveTime?: number, strength?: number, widthScale?: number, scaleUi?: number, sunset?: number }} opts
 */
export function drawSeaGlare(ctx, bounds, opts) {
  if (!ctx || !bounds || !opts) return;
  var seaLineY = Number(opts.seaLineY);
  if (!isFinite(seaLineY) || seaLineY > bounds.y + bounds.height) return;
  var seaH = bounds.y + bounds.height - seaLineY;
  var scaleUi = Number(opts.scaleUi);
  if (!(scaleUi > 0)) scaleUi = Math.max(0.5, bounds.width / 720);
  var waveTime = opts.waveTime != null ? Number(opts.waveTime) : timeSec(opts.currentTime);
  var rows = layoutSeaGlare({
    celestialX: opts.celestialX,
    seaLineY: seaLineY,
    seaH: seaH,
    scaleUi: scaleUi,
    isMoon: !!opts.isMoon,
    strength: opts.strength,
    widthScale: opts.widthScale,
    waveTime: waveTime,
  });
  if (!rows.length || typeof ctx.stroke !== "function") return;
  var cols = glareColors(!!opts.isMoon, opts.sunset || 0);
  var lw = horizonLineWidth(scaleUi);
  ctx.save();
  ctx.beginPath();
  ctx.rect(bounds.x, seaLineY + lw * 0.5, bounds.width, seaH);
  ctx.clip();
  ctx.lineCap = "round";
  for (var i = 0; i < rows.length; i++) {
    var g = rows[i];
    if (g.alpha <= 0.01) continue;
    ctx.globalAlpha = g.alpha;
    ctx.strokeStyle = rgb(cols.tint);
    ctx.lineWidth = g.thick;
    ctx.beginPath();
    ctx.moveTo(g.x - capInner(g.half, g.thick), g.y);
    ctx.lineTo(g.x + capInner(g.half, g.thick), g.y);
    ctx.moveTo(g.satX - capInner(g.satHalf, g.thick), g.y);
    ctx.lineTo(g.satX + capInner(g.satHalf, g.thick), g.y);
    ctx.stroke();
    ctx.strokeStyle = rgb(cols.core);
    ctx.lineWidth = g.thick * 0.5;
    ctx.beginPath();
    ctx.moveTo(g.x - capInner(g.half * 0.62, g.thick * 0.5), g.y);
    ctx.lineTo(g.x + capInner(g.half * 0.62, g.thick * 0.5), g.y);
    ctx.stroke();
  }
  ctx.restore();
}

/** Half-length of a round-capped stroke's straight part, so the caps land on ±half. */
function capInner(half, thick) {
  return Math.max(0.01, half - thick * 0.5);
}
